import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  DollarSign,
  PercentIcon,
  X,
  ArrowRight,
} from "lucide-react";
import { useQuoteRequestState } from "../hooks/useAnalysisState";
import { FileUpload } from "../components/FileUpload";

interface QuoteDetails {
  rate: string;
  lenderFees: string;
  mortgageInsurance: string;
}

const lenderFeesList = [
  { name: "Origination Fee" },
  { name: "Appraisal Fee" },
  { name: "Credit Report Fee" },
  { name: "Discount Points" },
  { name: "Underwriting Fee" },
  { name: "Processing Fee" },
  { name: "Application Fee" },
  { name: "Tax Service" },
  { name: "Flood Certification" },
  { name: "VOE" },
];

const titleFees = [
  { name: "Title Search" },
  { name: "Title Insurance" },
  { name: "Recording Fees" },
  { name: "Settlement Fee" },
  { name: "Notary Fee" },
  { name: "MERS" },
];

function formatNumber(value: string | number): string {
  const number =
    typeof value === "number" ? value : parseFloat(value.replace(/,/g, ""));
  if (isNaN(number)) return "";
  return number.toLocaleString();
}

function FeeInput({
  value,
  onChange,
  placeholder = "e.g., 1,000",
}: {
  value: number;
  onChange: (num: number) => void;
  placeholder?: string;
}) {
  const [displayValue, setDisplayValue] = useState(
    isNaN(value) ? "" : formatNumber(value)
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDisplayValue(raw);

    const numericString = raw.replace(/[^0-9.-]/g, ""); // Remove non-numeric characters
    const parsed = parseFloat(numericString);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    // Re-format input on blur
    const numericString = displayValue.replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(numericString);
    if (!isNaN(parsed)) {
      setDisplayValue(formatNumber(parsed));
    } else {
      setDisplayValue("");
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className="input-field pl-10 w-full"
    />
  );
}

const CheckMyRate: React.FC = () => {
  const navigate = useNavigate();
  const [fileKey, setFileKey] = useState<string | null>(null);
  const { quoteDetails, setQuoteDetails, questions, setQuestions } =
    useQuoteRequestState();
  const [showLenderFees, setShowLenderFees] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);

  const lenderFeesValue = quoteDetails.lenderFees
    ? parseFloat(quoteDetails.lenderFees)
    : NaN;

  const handleFileUploaded = (key: string) => {
    setFileKey(key);
    setShowQuestions(true);
  };

  const addQuestion = () => {
    setQuestions([...questions, ""]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, value: string) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    setQuestions(newQuestions);
  };

  const handleQuoteChange = (field: keyof QuoteDetails, value: string) => {
    const newDetails = { ...quoteDetails, [field]: value };
    setQuoteDetails(newDetails);
    // Check if all required fields are filled
    if (newDetails.rate) {
      setShowQuestions(true);
    } else {
      setShowQuestions(false);
    }
  };

  const isQuoteComplete = () => {
    if (fileKey) return true;

    const hasLenderFees =
      quoteDetails.lenderFees !== undefined &&
      quoteDetails.lenderFees !== null &&
      quoteDetails.lenderFees !== "";

    return quoteDetails.rate && hasLenderFees;
  };

  const handleBack = () => {
    if (showManualEntry) {
      setShowManualEntry(false);
      setShowQuestions(false);
    } else {
      navigate(-1);
    }
  };

  const handleContinue = () => {
    navigate("/loan-information");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Enter Details About Your Mortgage Quote
          </h1>

          <div className="flex items-center justify-center mb-4">
            <Link to="/example-results" className="btn-tertiary flex">
              <span>What will this get me?</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          {/* PDF Upload Section */}
          {!showManualEntry && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Upload Your Quote or Loan Estimate (Optional)
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                If you have a Loan Estimate, that's perfect! If not, make sure
                that your uploaded quote has your interest rate (not APR) and a
                breakdown of lender fees.
              </p>
              <FileUpload
                onFileUploaded={handleFileUploaded}
                onManualEntryClick={() => setShowManualEntry(true)}
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                maxSize={10 * 1024 * 1024} // 10MB
              />
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-gray-500">
                    OR
                  </span>
                </div>
              </div>
              <div className="text-center">
                <button
                  onClick={() => setShowManualEntry(true)}
                  className="btn-tertiary"
                >
                  Enter information manually instead
                </button>
              </div>
            </div>
          )}

          {/* Manual Quote Entry Section */}
          {showManualEntry && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Enter Quote Details Manually
                </h2>
                {/* <button
                  onClick={() => {
                    setShowManualEntry(false);
                    setShowQuestions(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button> */}
              </div>

              {/* Interest Rate */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interest Rate
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PercentIcon size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={quoteDetails.rate}
                    onChange={(e) => handleQuoteChange("rate", e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="e.g., 5.25"
                    className="input-field pl-10 w-full  [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter interest rate, not APR
                </p>
              </div>

              {/* Lender Fees */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Total Lender Fees
                  </label>
                  <button
                    onClick={() => setShowLenderFees(!showLenderFees)}
                    className="btn-tertiary flex items-center"
                  >
                    {/* {showLenderFees ? (
                      <>
                        Hide Common Lender Fees
                        <ChevronUp className="w-4 h-4 ml-1" />
                      </>
                    ) : (
                      <>
                        Show Common Lender Fees
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </>
                    )} */}
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign size={18} className="text-gray-400" />
                  </div>
                  <FeeInput
                    value={lenderFeesValue}
                    onChange={(num) =>
                      handleQuoteChange("lenderFees", String(num))
                    }
                    placeholder="e.g., 2,500"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  If you have a Loan Estimate, add together the totals from
                  sections A and B. Enter negative number for credits, positive
                  for fees.
                </p>
                {showLenderFees && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg ">
                    <h3 className="text-sm font-medium text-gray-900 mb-2 ">
                      Common Lender Fees:
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {lenderFeesList.map((fee) => (
                        <div key={fee.name} className="flex items-start">
                          <span className="text-sm text-gray-900">
                            • {fee.name}
                          </span>
                        </div>
                      ))}
                    </div>
                    <h3 className="text-sm font-medium pt-4 text-red-500 mb-2">
                      Lender fees do not include:
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {titleFees.map((fee) => (
                        <div key={fee.name} className="flex items-start">
                          <span className="text-sm text-red-500">
                            • {fee.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mortgage Insurance */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Mortgage Insurance
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign size={18} className="text-gray-400" />
                  </div>
                  <FeeInput
                    value={
                      quoteDetails.mortgageInsurance
                        ? parseFloat(quoteDetails.mortgageInsurance)
                        : NaN
                    }
                    onChange={(num) =>
                      handleQuoteChange("mortgageInsurance", String(num))
                    }
                    placeholder="e.g., 175"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter your monthly mortgage insurance payment (PMI) if
                  applicable
                </p>
              </div>
            </div>
          )}

          {/* Questions Section */}
          {showQuestions && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Questions About Your Quote (Optional)
              </h2>
              <p className="text-gray-600 mb-4">
                Do you have any questions about your quote that you'd like a
                mortgage expert to answer?
              </p>

              {questions.map((question, index) => (
                <div key={index} className="flex items-center gap-4 mb-4">
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => updateQuestion(index, e.target.value)}
                    placeholder="Enter your question"
                    className="input-field w-full"
                  />
                  {questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(index)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}

              <button onClick={addQuestion} className="btn-tertiary">
                + Add another question
              </button>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <button onClick={handleBack} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={!isQuoteComplete()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Loan Information
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckMyRate;
